/**
 * Theme selection (design/tokens.json `meta.availableThemes`). Phase 1 ships
 * no switcher UI (that's Phase 2, per tokens.md SS0 scope note) — this module
 * only applies the default theme's `data-theme` attribute so the CSS theme
 * layer in tokens.css resolves. Kept as its own module (not inlined in
 * main.tsx) so a Phase 2 switcher has one obvious place to plug into.
 */

export type ThemeName = 'green' | 'orange';

export const DEFAULT_THEME: ThemeName = 'green';
export const AVAILABLE_THEMES: readonly ThemeName[] = ['green', 'orange'];

const STORAGE_KEY = 'compass:theme';

/**
 * Fired on `window` whenever `applyTheme()` changes the applied theme —
 * lets components that read `getStoredTheme()` at render time (e.g.
 * `ThemeIndicatorDot`) stay in sync with a sibling `ThemeSwitcher` without a
 * shared context/provider, matching this module's existing "small function
 * pair over localStorage" shape rather than introducing new surface area.
 */
export const THEME_CHANGE_EVENT = 'compass:theme-change';

export function getStoredTheme(): ThemeName {
  if (typeof window === 'undefined') return DEFAULT_THEME;
  const stored = window.localStorage?.getItem(STORAGE_KEY);
  if (stored === 'green' || stored === 'orange') return stored;
  return DEFAULT_THEME;
}

export function applyTheme(theme: ThemeName): void {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('data-theme', theme);
  window.localStorage?.setItem(STORAGE_KEY, theme);
  window.dispatchEvent(new CustomEvent(THEME_CHANGE_EVENT, { detail: theme }));
}
